// Session-scoped page/journey context for the help assistant (HelpChat).
//
// The chat widget lives in the root layout, a sibling of every page, so it can't
// read page state through props. This module is the shared channel: pages and the
// builder publish what they know (live builder state, page-specific starter
// questions), navigation and search are recorded centrally, and HelpChat reads an
// assembled snapshot at send time to (a) pick context-aware starter prompts and
// (b) attach a context block to /api/chat so answers know where the user is.
//
// Everything here is session-only and best-effort — it never throws into the UI,
// and nothing personal is exposed (booleans like hasSignedUp, never the email).

const NAV_KEY = "rd_ctx_nav";
const SEARCH_KEY = "rd_ctx_search";
const NAV_MAX = 8;
const SEARCH_MAX = 5;

export type NavEntry = { path: string; title: string };

export type BuilderState = {
  /** Human task label of the open report, e.g. "Boiler installation". */
  report: string;
  category: string;
  trade: string;
  /** Coarse stage of the build, for starter selection and answer framing. */
  stage: "choosing" | "filling" | "review";
  readiness: number;
  /** Short reviewer actions still outstanding (already-phrased, capped). */
  missing: string[];
  /** Download is blocked behind the email gate. */
  emailGated: boolean;
};

/** Assembled snapshot handed to HelpChat — drives starters and the chat context block. */
export type ChatContext = {
  path: string;
  title: string;
  builder?: BuilderState;
  /** Topic of the current content page (e.g. a template title), if it published one. */
  topic?: string;
  /** Page-published starter questions (e.g. a template's FAQ). */
  pageStarters?: string[];
  recentPages: NavEntry[];
  recentSearches: string[];
  utmTerm?: string;
  device: "mobile" | "desktop";
  hasSignedUp: boolean;
  hasDraft: boolean;
  lastReport?: string;
};

// --- In-memory channels (republished by the owning component on mount) ---------

let builderState: BuilderState | null = null;
let pageTopic: string | null = null;
let pageStarters: string[] | null = null;
let lastReport: string | null = null;

/** Builder calls this on every relevant state change; pass null on unmount. */
export function setBuilderState(state: BuilderState | null): void {
  builderState = state;
}

/** A content page publishes its topic + starter questions (e.g. template FAQ). */
export function setPageStarters(topic: string | null, starters: string[] | null): void {
  pageTopic = topic;
  pageStarters = starters && starters.length ? starters.slice(0, 6) : null;
}

/** Builder records the last report a user actually downloaded, for "pick up where you left off". */
export function setLastReport(report: string): void {
  lastReport = report || null;
}

// --- Session ring buffers ------------------------------------------------------

function readBuffer<T>(key: string): T[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeBuffer(key: string, arr: unknown[]): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(arr));
  } catch {
    /* private mode / quota — context is optional */
  }
}

/** Record a page view (deduped against the most recent). Called on route change. */
export function recordNav(path: string, title: string): void {
  if (typeof window === "undefined" || !path) return;
  const nav = readBuffer<NavEntry>(NAV_KEY);
  const last = nav[nav.length - 1];
  if (last && last.path === path) {
    // Same path, possibly a later/better title — keep the richer one.
    if (title && title.length > last.title.length) last.title = title.slice(0, 120);
    writeBuffer(NAV_KEY, nav);
    return;
  }
  nav.push({ path, title: (title || "").slice(0, 120) });
  writeBuffer(NAV_KEY, nav.slice(-NAV_MAX));
}

/** Record a search query the user submitted (deduped against the most recent). */
export function recordSearch(query: string): void {
  if (typeof window === "undefined") return;
  const q = (query || "").trim().slice(0, 120);
  if (q.length < 2) return;
  const searches = readBuffer<string>(SEARCH_KEY);
  if (searches[searches.length - 1] === q) return;
  searches.push(q);
  writeBuffer(SEARCH_KEY, searches.slice(-SEARCH_MAX));
}

// --- Snapshot ------------------------------------------------------------------

function has(key: string): boolean {
  try {
    return !!localStorage.getItem(key);
  } catch {
    return false;
  }
}

/** Assemble the current context snapshot. Best-effort; safe to call any time. */
export function getChatContext(): ChatContext {
  if (typeof window === "undefined") {
    return { path: "", title: "", recentPages: [], recentSearches: [], device: "desktop", hasSignedUp: false, hasDraft: false };
  }
  const utmTerm = new URLSearchParams(window.location.search).get("utm_term") || undefined;
  return {
    path: window.location.pathname,
    title: document.title,
    builder: builderState || undefined,
    topic: pageTopic || undefined,
    pageStarters: pageStarters || undefined,
    recentPages: readBuffer<NavEntry>(NAV_KEY),
    recentSearches: readBuffer<string>(SEARCH_KEY),
    utmTerm,
    device: window.matchMedia && window.matchMedia("(max-width: 1023px)").matches ? "mobile" : "desktop",
    hasSignedUp: has("ramsdocs_email"),
    hasDraft: has("ramsdocs_builder_draft"),
    lastReport: lastReport || undefined,
  };
}
